import { IPInformation } from '../../api';
import { MediaType } from '../../utils/media';
import { LocationInfo } from '../LocationInfo';
import { ContentSource } from '../ContentSource';
import { ConversionRule, ConversionType } from './Conversion';
import {
    BusinessContactInfo,
    IAccountBetas,
    IAccountMapSettings,
    JobLocationDisplay,
} from 'models/Account';
import { ILabel, LabelSummary, WidgetConfigResponse } from 'models';
import { IMAGE_ORIENTATION } from '../Media';

export const MAXIMUM_CONTENT_DISTANCE_FROM_CENTER = 450;
export const MAXIMUM_WORKSITES_TO_SHOW = 250;
export const DEFAULT_ICON_COLOR = '#484848';

export type LabelSummaryWithUsage = LabelSummary & { timesUsed: number };

export interface PluginAccountInfo {
    id: string;
    name: string;
    sourceUrls?: Partial<Record<ContentSource, string>>;
    geolocation?: google.maps.LatLngLiteral | undefined;
    address?: string;
    mapSettings?: IAccountMapSettings;
    betas?: IAccountBetas[];
    cityMap?: Record<string, number>;
    tagMap?: Record<string, number>;
    labelsWithUsage?: LabelSummaryWithUsage[];
    showPoweredBy?: boolean;
    businessContactInfo?: BusinessContactInfo;
}

export interface PluginContentData {
    workSites: PluginWorkSite[];
}

export type ContentFilters = {
    location?: string | string[];
    preferredMessage?: true;
    requiredMedia?: MediaType; // specify a specific type of media
    needsMedia?: boolean; // needs any kind of media
    needsReview?: boolean; // needs review
    needsDescription?: boolean;
    showcaseOnly?: boolean;
    tags?: string[];
};

export interface PluginContentRequest {
    contentType: PluginContentType;
    contentFilters?: ContentFilters;
    limit?: number;
    skip?: number;
    showcaseOnly?: boolean;
}
export interface PluginConfigResponse {
    account: PluginAccountInfo;
    ipInfo?: IPInformation;
    pages: FeatureSettings[];
    conversionRules: ConversionRule[];
    googleMapsKey: string;
    globalSettings: WebGlobalSettings;
    businessContactInfo?: BusinessContactInfo;
    portfolioWidgetExtraData?: ExtraSalestoolConfigResponse;
    neighborhoodWidgetExtraData?: WidgetConfigResponse;
}

export interface ExternalSystemIntegration {
    type: 'Google' | 'Facebook';
    url: string;
    reviewCount: number;
    reviewRating: number;
}

export interface ExtraSalestoolConfigResponse {
    logo?: string;
    contactFormUrl?: string;
    website?: string;
    name?: string;
    email?: string;
    phone?: string;
    integrations: ExternalSystemIntegration[];
    showcaseOnly?: boolean;
}

interface PluginFeatureSettings {
    id: string;
    description?: string;
    pageSize?: number;
    destroyMap?: boolean;
    loaderScriptUrl?: string;
    widgetEntryMethodName?: string;
}

export interface TargetingFeatureSettings {
    path: string;
    target: string;
}

export interface FilterFeatureSettings {
    location?: string[];
    labels?: ILabel['externalId'][];
}

export interface CallToActionFeatureSettings {
    callToAction?: {
        href: string;
        label: string; // can be HTML
    };
}

export interface WebGlobalSettings {
    redirectPath?: string;
    callToActionHref?: string;
    callToActionLabel?: string;
    callToActionConversionType?: ConversionType;

    cssOverride?: string;
}

export interface MapFeatureSettings {
    googleMapId?: string;
    mapMarkerColor?: string;
    zoom?: {
        min: number;
        max: number;
        default: number;
    };
}

export type FeatureSettings =
    | PageWidgetFeatureSettings
    | CarouselWidgetFeatureSettings
    | MapWidgetFeatureSettings
    | PortfolioWidgetFeatureSettings
    | NeighborhoodWidgetFeatureSettings;

export enum PluginContentType {
    ReviewOnly = 'ReviewOnly',
    SiteProofOnly = 'SiteProofOnly',
    VideoOnly = 'VideoOnly',
    Default = 'Default',
}

export interface CarouselWidgetFeatureSettings
    extends PluginFeatureSettings,
        TargetingFeatureSettings,
        FilterFeatureSettings {
    type: 'Carousel Widget';

    contentType: PluginContentType;
    size?: number;
    rows?: number;
    disableSchemaMaps: boolean;
}

export interface PortfolioWidgetFeatureSettings
    extends PluginFeatureSettings,
        TargetingFeatureSettings,
        FilterFeatureSettings {
    type: 'Portfolio Widget';
    enableFilters: boolean;
    enableHeader: boolean;
    widgetHeight: string;
}

export interface NeighborhoodWidgetFeatureSettings
    extends PluginFeatureSettings,
        TargetingFeatureSettings,
        FilterFeatureSettings {
    type: 'Neighborhood Widget';
    enableFilters: boolean;
    enableHeader: boolean;
    widgetHeight: string;
}

export interface MapWidgetFeatureSettings
    extends MapFeatureSettings,
        PluginFeatureSettings,
        FilterFeatureSettings,
        TargetingFeatureSettings {
    type: 'Map Widget';
    enableReview: boolean;
    enableCheckins: boolean;
    enableAreas: boolean;
}

export interface PageWidgetFeatureSettings
    extends TargetingFeatureSettings,
        PluginFeatureSettings,
        FilterFeatureSettings,
        MapFeatureSettings {
    type: 'Page Widget';
    enableReview: boolean;
    enableCheckins: boolean;
    enableMap: boolean;
    enableAreas: boolean;
    enableJobLocations: boolean;
    enableSEOData: boolean;
    enableCanonicalUrls: boolean;
    disableSchemaMaps: boolean;
}

export interface PageWidgetSEOData {
    description?: string;
    businessName: string;
    primaryCategory: string;
    secondaryCategories?: string[];
    address?: any;
}

export interface PluginGeoLocation {
    latitude: number;
    longitude: number;
}

export interface PluginMedia {
    thumbnailUrl?: string; // not all pictures have a thumbnail
    sourceUrl: string;
    type: MediaType;
    id: string;
    altText?: string;
    sortOrder: number;
    isCover?: boolean;
    takenAtMillis?: number;
    orientation?: IMAGE_ORIENTATION;
    isBeforeMedia?: boolean;
    isAfterMedia?: boolean;
}

// Causes load issues using the actual google enum
export const PluginMapPositions = {
    BOTTOM: 11,
    BOTTOM_CENTER: 11,
    BOTTOM_LEFT: 10,
    BOTTOM_RIGHT: 12,
    CENTER: 13,
    LEFT: 5,
    LEFT_BOTTOM: 6,
    LEFT_CENTER: 4,
    LEFT_TOP: 5,
    RIGHT: 7,
    RIGHT_BOTTOM: 9,
    RIGHT_CENTER: 8,
    RIGHT_TOP: 7,
    TOP: 2,
    TOP_CENTER: 2,
    TOP_LEFT: 1,
    TOP_RIGHT: 3,
};

export interface PluginWorkSite {
    id: string;
    dateInMillis: number;
    geolocation?: google.maps.LatLngLiteral | undefined;
    location?: string;
    locationInfo?: Omit<
        LocationInfo,
        'address1' | 'address2' | 'googlePlaceId' | 'googlePlusCode'
    >; // address is too specific for the plugin
    staticMapUrl?: string;
    distanceInMiles?: number;
    content: PluginContent[];
    description?: string;
    sortDateInMillis?: number;
    labels?: LabelSummary[];
    title?: string;
    showcase?: boolean;
}

export type PluginContent = PluginCheckin | PluginReview;

interface PluginContentBase {
    id: string;
    message: string;
    dateInMillis: number;
    source?: ContentSource;
    media: PluginMedia[];
    sortOrder?: number;
    takenAtMillis?: number;
}

export interface PluginCheckin extends PluginContentBase {
    type: 'Checkin';
    technician: string;
}

export interface PluginReview extends PluginContentBase {
    type: 'Review';
    rating: number;
    author: string;
    authorPhotoUrl?: string;
    response?: {
        postedAtInMillis: number;
        message: string;
    };
}

export enum PageRuleType {
    PageWidget = 'Page Widget',
    CarouselWidget = 'Carousel Widget',
    MapWidget = 'Map Widget',
    PortfolioWidget = 'Portfolio Widget',
    NeighborhoodWidget = 'Neighborhood Widget',
}

export type PluginJobDetails = {
    job: PluginWorkSite;
    similarJobs: PluginWorkSite[];
    showcaseOnly?: boolean;
};

export interface PrivacySettings {
    hideHomeownerName: boolean;
    jobLocationDisplay: JobLocationDisplay;
}
